import { Injectable } from '@angular/core';
import { MapInfo } from '../models/map-marker';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  constructor(private notifications: NotificationService) { }

  getCurrentLocation(): Promise<MapInfo> {
    return this.getFreshPosition();
  }

  getFreshPosition(maxAgeMs = 5000, totalTimeoutMs = 15000): Promise<MapInfo> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject('Geolocation is not supported by this browser.');
        return;
      }

      let watchId: number | null = null;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let waitingToastId: ReturnType<typeof setTimeout> | null = null;
      let waitingToastShown = false;
      let bestSoFar: GeolocationPosition | null = null;
      let settled = false;

      const cleanup = () => {
        if (watchId !== null) {
          navigator.geolocation.clearWatch(watchId);
          watchId = null;
        }
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (waitingToastId !== null) {
          clearTimeout(waitingToastId);
          waitingToastId = null;
        }
      };

      const settle = (info: MapInfo) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(info);
      };

      waitingToastId = setTimeout(() => {
        if (!settled && !waitingToastShown) {
          waitingToastShown = true;
          this.notifications.showInfo('Posizione non aggiornata, attendere…');
        }
      }, 3000);

      timeoutId = setTimeout(() => {
        if (settled) return;
        if (bestSoFar) {
          const now = Date.now();
          settle({
            latitudine: bestSoFar.coords.latitude,
            longitudine: bestSoFar.coords.longitude,
            precisione: bestSoFar.coords.accuracy,
            data: new Date(now),
            gpsFixTimestamp: new Date(bestSoFar.timestamp),
            fixAgeMs: now - bestSoFar.timestamp,
            staleFix: true
          });
        } else {
          settle({ error: 'Timeout: posizione non disponibile' });
        }
      }, totalTimeoutMs);

      watchId = navigator.geolocation.watchPosition(
        (position) => {
          if (settled || !position) return;

          const clientNow = Date.now();
          const fixAge = clientNow - position.timestamp;

          if (fixAge <= maxAgeMs) {
            settle({
              latitudine: position.coords.latitude,
              longitudine: position.coords.longitude,
              precisione: position.coords.accuracy,
              data: new Date(clientNow),
              gpsFixTimestamp: new Date(position.timestamp),
              fixAgeMs: fixAge,
              staleFix: false
            });
            return;
          }

          if (!bestSoFar || position.timestamp > bestSoFar.timestamp) {
            bestSoFar = position;
          }
        },
        (error) => {
          if (settled) return;
          if (bestSoFar) return;
          settle({ error: error.message });
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
      );
    });
  }
}
